import { Expose, Transform, Type } from 'class-transformer';
import { dateTransformer } from 'src/lib/utilities/api/class-transformer-types';
import { PagingSearchFieldArgument } from 'src/lib/utilities/api/patterns/pagination/paging-search-field.argument';
import {
	CurriculumSectionMappingReplacementModel,
	CurriculumSectionMappingReplacementModel_ClassTransform_Type,
} from '../curriculum-manager-replacement.model';

export class CurriculumCourseStateCodeModel {
	state: string;
	code: string;
	inherited?: boolean;
}

export function CurriculumCourseStateCodeModel_ClassTransform_Type() {
	return CurriculumCourseStateCodeModel;
}

export class CurriculumCourseDeactivatedModel {
	by_uid: number;
	by_user: string;
	@Expose({ name: 'timestamp' })
	@Transform(dateTransformer)
	date: Date;
}

export function CurriculumCourseDeactivatedModel_ClassTransform_Type() {
	return CurriculumCourseDeactivatedModel;
}

export class CurriculumCourseOverviewAssociationsModel {
	catalog_count: number;
	graduation_plan_count: number;
	version_count: number;
	teacher_count: number;
	student_count: number;
	is_deletable: boolean;
}

export function CurriculumCourseOverviewAssociationsModel_ClassTransform_Type() {
	return CurriculumCourseOverviewAssociationsModel;
}

export class CurriculumUserChangeModel {
	by_uid: number;
	by_user: string;
	@Expose({ name: 'timestamp' })
	@Transform(dateTransformer)
	date: Date;
}

export function CurriculumUserChangeModel_ClassTransform_Type() {
	return CurriculumUserChangeModel;
}

export class CurriculumCourseOverviewModel {
	id: number;
	title: string;
	active: boolean;
	course_number: string;
	description: string;
	@Type(CurriculumUserChangeModel_ClassTransform_Type)
	created: CurriculumUserChangeModel;
	@Type(CurriculumUserChangeModel_ClassTransform_Type)
	last_edited: CurriculumUserChangeModel;
	@Type(CurriculumCourseDeactivatedModel_ClassTransform_Type)
	deactivated: CurriculumCourseDeactivatedModel;
	default_base_subject_areas: number[];
	@Type(CurriculumCourseStateCodeModel_ClassTransform_Type)
	state_codes: CurriculumCourseStateCodeModel[];
	@Type(CurriculumCourseOverviewAssociationsModel_ClassTransform_Type)
	associations: CurriculumCourseOverviewAssociationsModel;
	suggestion_rank: number;
}

export function CurriculumCourseOverviewModel_ClassTransform_Type() {
	return CurriculumCourseOverviewModel;
}

export class CurriculumCourseOverviewResponseModel {
	@Type(CurriculumCourseOverviewModel_ClassTransform_Type)
	results: CurriculumCourseOverviewModel[];
	count: number;
}

export class CurriculumCourseVersionSectionLMSModel {
	lms_id: number;
	lms_title: string;
	lms_section_id: string;
	lms_url: null;
}

export function CurriculumCourseVersionSectionLMSModel_ClassTransform_Type() {
	return CurriculumCourseVersionSectionLMSModel;
}

export class CurriculumCourseVersionAssociatedModel {
	catalog_count: number;
	section_count?: number; // these 2 are only available in view version call
	is_deletable?: boolean;
}

export function CurriculumCourseVersionAssociatedModel_ClassTransform_Type() {
	return CurriculumCourseVersionAssociatedModel;
}

export class CurriculumCourseVersionSectionModel {
	active: boolean;
	section_id: number;
	section_title: string;
	term: number;
	term_type: string;
	@Type(CurriculumCourseVersionSectionLMSModel_ClassTransform_Type)
	lms_mapping: CurriculumCourseVersionSectionLMSModel;
	@Type(CurriculumCourseVersionAssociatedModel_ClassTransform_Type)
	associated: CurriculumCourseVersionAssociatedModel;
	@Type(CurriculumSectionMappingReplacementModel_ClassTransform_Type)
	replacements: CurriculumSectionMappingReplacementModel;
}

export function CurriculumCourseVersionSectionModel_ClassTransform_Type() {
	return CurriculumCourseVersionSectionModel;
}

export class CurriculumCourseVersionModel {
	id: number;
	course_id: number;
	active: boolean;
	title: string;
	code: string;
	sced_code: string;
	description: string;
	notes: string;
	overridden_description: string;
	overridden_title: string;
	overridden_suggestion_rank: number;
	@Type(CurriculumUserChangeModel_ClassTransform_Type)
	created: CurriculumUserChangeModel;
	@Type(CurriculumUserChangeModel_ClassTransform_Type)
	last_edited: CurriculumUserChangeModel;
	@Type(CurriculumCourseDeactivatedModel_ClassTransform_Type)
	deactivated: CurriculumCourseDeactivatedModel;
	@Type(CurriculumCourseStateCodeModel_ClassTransform_Type)
	state_codes: CurriculumCourseStateCodeModel[];
	@Type(CurriculumCourseVersionSectionModel_ClassTransform_Type)
	sections: CurriculumCourseVersionSectionModel[];
	@Type(CurriculumCourseVersionAssociatedModel_ClassTransform_Type)
	associated: CurriculumCourseVersionAssociatedModel;
	suggestion_rank: number;
}

export function CurriculumCourseVersionModel_ClassTransform_Type() {
	return CurriculumCourseVersionModel;
}

export class CurriculumCourseModel extends CurriculumCourseOverviewModel {
	@Type(CurriculumCourseVersionModel_ClassTransform_Type)
	versions: CurriculumCourseVersionModel[];
}

export interface CurriculumCourseSearchArgument {
	id: PagingSearchFieldArgument;
	title: PagingSearchFieldArgument;
	catalog_count: PagingSearchFieldArgument;
	course_number: PagingSearchFieldArgument;
	version_count: PagingSearchFieldArgument;
	teacher_count: PagingSearchFieldArgument;
	graduation_plan_count: PagingSearchFieldArgument;
	student_count: PagingSearchFieldArgument;
	created: PagingSearchFieldArgument;
	last_edited: PagingSearchFieldArgument;
	deactivated: PagingSearchFieldArgument;
	active: { value: boolean };
}

export interface CurriculumCourseStateCodeArgument {
	state: string;
	code: string;
}

export interface CurriculumCourseUpsertArgument {
	title: string;
	course_number: string;
	description: string;
	default_base_subject_areas: number[];
	suggestion_rank: number;
	state_codes?: CurriculumCourseStateCodeArgument[];
}

export interface CurriculumVersionEditArgument {
	title: string;
	code: string;
	description: string;
	notes: string;
	sced_code: string;
	suggestion_rank: number;
	state_codes?: CurriculumCourseStateCodeArgument[];
}

export interface CurriculumVersionCreateArgument extends Partial<CurriculumVersionEditArgument> {
	course_id: number;
}
